/**
 * Search that forgives what happens at the bar: typos ("chianty"), missing accents and umlauts
 * ("rose", "weissburgunder"), words in any order ("pfalz grauburgunder"). Every word of the query
 * has to fit somewhere in the text; a closer fit ranks higher.
 */

/**
 * Lower case, umlauts and accents spelled out, everything else a space. The Turkish dotless ı has no
 * accent to drop, it is an i („kir“ finds „Kır pidesi“); İ falls apart into i and its dot.
 */
export function fold(text: string): string {
  return text
    .toLowerCase()
    .replace(/ı/g, "i")
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** Edit distance with a ceiling: stops early once it is above `max`. */
export function distance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  let before: number[] = [];
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      let d = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
      // Two letters swapped ("chainti") count as one mistake.
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d = Math.min(d, before[j - 2]! + 1);
      cur[j] = d;
      best = Math.min(best, d);
    }
    if (best > max) return max + 1;
    before = prev;
    prev = cur;
  }
  return prev[b.length]!;
}

/** How many mistakes a word may have: none for short ones and numbers, so "47" and "2015" stay exact. */
const allowed = (word: string) => (/\d/.test(word) || word.length <= 3 ? 0 : word.length <= 6 ? 1 : 2);

/** How well one query word fits the text's words: 0 best, null not at all. */
function wordScore(word: string, words: string[], text: string): number | null {
  if (words.some((w) => w === word)) return 0;
  if (words.some((w) => w.startsWith(word))) return 1;
  if (text.includes(word)) return 2;
  const max = allowed(word);
  if (max === 0) return null;
  let best: number | null = null;
  for (const w of words) {
    // A typed word may be the beginning of a longer one ("chiant" for "chianti").
    const candidates = w.length > word.length ? [w, w.slice(0, word.length)] : [w];
    for (const c of candidates) {
      const d = distance(word, c, max);
      if (d <= max) best = Math.min(best ?? Infinity, 2 + d);
    }
  }
  return best;
}

/**
 * A score for `text` against `query`, lower is better, or null when a word does not fit at all.
 * An empty query fits everything with 0.
 */
export function searchScore(text: string, query: string): number | null {
  const words = fold(query).split(" ").filter(Boolean);
  if (words.length === 0) return 0;
  const folded = fold(text);
  const tokens = folded.split(" ");
  let total = 0;
  for (const word of words) {
    const s = wordScore(word, tokens, folded);
    if (s === null) return null;
    total += s;
  }
  return total;
}
